import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { SlideIn } from '@/components/transitions/FadeTransition';
import { toast } from "react-toastify";
import { AlertCircle } from 'lucide-react';
import NoteNextSteps from './NoteNextSteps';
import DynamicForm from '@/components/templates/DynamicForm';
import EncounterPhotoUpload from '@/features/notes/components/EncounterPhotoUpload';
import { getWithAuth, buildApiUrl, deleteRequest } from '@/utils/apiClient';
import { getNoteTypeLabel } from '@/features/notes/utils/noteTypeUtils';
import { logger } from "../../../utils/logger";
import { useDialog } from '@/contexts/DialogContext';

// Lazy to avoid a module cycle: the canonical editor surfaces the AI chat,
// and this queue surface embeds the canonical editor.
const NoteEditorView = lazy(() =>
  import('@/features/notes/NotePage').then((m) => ({ default: m.NoteEditorView }))
);

/**
 * Review surface for AI-generated drafts inside the assistant queue.
 * View + disposition only: manual editing and signing live in the canonical
 * /notes/:id editor; this panel navigates there with return context so the
 * provider can edit/sign and come back to the queue.
 */
function NotePreview({
  viewingNoteInline,
  setViewingNoteInline,
  inlineNoteData,
  setInlineNoteData,
  batchNotes,
  setBatchNotes,
  currentNoteIndex,
  setCurrentNoteIndex,
  fetchNoteData,
  onWriteNextNote,
  onDisposition
}) {
  const [template, setTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({});
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const { confirm } = useDialog();
  const [editing, setEditing] = useState(false);
  const editingNoteIdRef = useRef<string | null>(null);

  // Keep a snapshot of the last valid note data so children can render
  // safely during the SlideIn exit animation (when inlineNoteData is already null).
  const lastNoteDataRef = useRef(inlineNoteData);
  if (inlineNoteData) {
    lastNoteDataRef.current = inlineNoteData;
  }
  const noteData = inlineNoteData ?? lastNoteDataRef.current;

  // Leaving edit mode whenever the viewed note changes or the panel closes.
  useEffect(() => {
    setEditing(false);
    editingNoteIdRef.current = null;
  }, [noteData?.id, viewingNoteInline]);

  const isTemplateNote = noteData?.template_id && noteData?.structured_data;
  const hasBatchNotes = batchNotes && batchNotes.length > 1;
  const isTerminal =
    noteData?.status === 'signed' ||
    noteData?.status === 'co_signed' ||
    noteData?.status === 'incorporated' ||
    noteData?.normalized_status === 'signed' ||
    noteData?.normalized_status === 'co_signed' ||
    noteData?.normalized_status === 'incorporated';

  // Fetch user permissions for this note (drives the read-only banner copy).
  useEffect(() => {
    if (noteData?.id && inlineNoteData) {
      setIsLoadingPermissions(true);
      getWithAuth(`/api/notes/${noteData.id}/permissions`)
        .then(async (resp) => {
          if (resp.ok) {
            const permData = await resp.json();
            setPermissions(permData);
          } else {
            setPermissions({ can_edit: false, editable_sections: [], user_role: null });
          }
        })
        .catch((err) => {
          logger.error('Failed to fetch note permissions:', err);
          setPermissions({ can_edit: false, editable_sections: [], user_role: null });
        })
        .finally(() => setIsLoadingPermissions(false));
    }
  }, [noteData?.id, inlineNoteData]);

  useEffect(() => {
    if (isTemplateNote && noteData?.template_id && inlineNoteData) {
      setIsLoadingTemplate(true);
      getWithAuth(`/api/templates/${noteData.template_id}`)
        .then(async (resp) => {
          if (resp.ok) {
            const templateData = await resp.json();
            setTemplate(templateData);
            try {
              const parsedData = typeof noteData?.structured_data === 'string'
                ? JSON.parse(noteData.structured_data)
                : noteData?.structured_data;
              setTemplateFormData(parsedData || {});
            } catch (e) {
              logger.error('Failed to parse structured_data:', e);
              setTemplateFormData({});
            }
          }
        })
        .catch((err) => logger.error('Failed to load template:', err))
        .finally(() => setIsLoadingTemplate(false));
    }
  }, [isTemplateNote, noteData?.template_id, noteData?.structured_data, inlineNoteData]);

  const handleNavigateNote = async (direction) => {
    if (!hasBatchNotes || !fetchNoteData || !batchNotes) return;

    const batchList = batchNotes;
    const newIndex = direction === 'next'
      ? Math.min(currentNoteIndex + 1, batchList.length - 1)
      : Math.max(currentNoteIndex - 1, 0);

    if (newIndex !== currentNoteIndex) {
      const noteToLoad = batchList[newIndex];
      try {
        const noteData = await fetchNoteData(noteToLoad.note_id || noteToLoad.id);
        if (noteData) {
          setInlineNoteData(noteData);
          setCurrentNoteIndex(newIndex);
        }
      } catch (error) {
        logger.error('Error loading note:', error);
        toast.error('Failed to load note');
      }
    }
  };

  // Editing happens in the SAME canonical editor, embedded in this surface so
  // the batch queue keeps its look. onReturn flips back to the review and
  // refreshes the note so queue state reflects edits/signing.
  const handleOpenInEditor = useCallback(() => {
    if (!noteData?.id) return;
    editingNoteIdRef.current = noteData.id;
    setEditing(true);
  }, [noteData?.id]);

  const handleEditorReturn = useCallback(() => {
    const noteId = editingNoteIdRef.current;
    setEditing(false);
    editingNoteIdRef.current = null;
    if (noteId && fetchNoteData) {
      fetchNoteData(noteId)
        .then((data) => {
          if (data) setInlineNoteData(data);
        })
        .catch((err) => logger.error('Failed to refresh note after editing:', err));
    }
  }, [fetchNoteData, setInlineNoteData]);

  const handleKeepDraft = useCallback(() => {
    // The draft is already persisted by generation — this is a pure
    // queue disposition ("keep as draft, move to the next work product").
    const nextIndex = currentNoteIndex + 1;
    if (batchNotes && nextIndex < batchNotes.length) {
      const nextNote = batchNotes[nextIndex];
      if (nextNote && fetchNoteData) {
        fetchNoteData(nextNote.note_id || nextNote.id)
          .then((data) => {
            if (data) {
              setInlineNoteData(data);
              setCurrentNoteIndex(nextIndex);
            }
          })
          .catch((error) => {
            logger.error('Error loading next draft:', error);
            toast.error('Failed to load next draft');
          });
        return;
      }
      setCurrentNoteIndex(nextIndex);
      return;
    }

    setViewingNoteInline(false);
    if (onDisposition) {
      onDisposition('save', noteData?.encounter_id, noteData?.id);
    } else if (onWriteNextNote) {
      onWriteNextNote();
    }
  }, [noteData, currentNoteIndex, batchNotes, setViewingNoteInline, setInlineNoteData, setCurrentNoteIndex, fetchNoteData, onWriteNextNote, onDisposition]);

  const handleDiscardDraft = useCallback(async () => {
    try {
      const noteId = noteData?.id || batchNotes?.[currentNoteIndex]?.id || batchNotes?.[currentNoteIndex]?.note_id;
      if (!noteId) {
        toast.error('No draft to discard.');
        return;
      }

      const siblingCount = (batchNotes?.length || 0) - 1;
      const remainingMessage = siblingCount > 0
        ? ` ${siblingCount} other draft${siblingCount > 1 ? 's' : ''} will remain for review.`
        : '';
      const confirmed = await confirm({
        title: 'Discard Draft',
        message: `Discard this unsigned generated draft?${remainingMessage} This cannot be undone.`,
        confirmText: 'Discard',
        variant: 'danger',
      });
      if (!confirmed) {
        return;
      }

      const resp = await deleteRequest(`/api/notes/${noteId}/delete`);
      if (!resp.ok) {
        logger.error(`Failed to discard note ${noteId}: ${resp.statusText}`);
        toast.error('Failed to discard draft.');
        return;
      }

      // Remove the discarded draft from the parent batch so the rest of the
      // review flow (navigation, dispositions) always points at the right
      // generated work product.
      const remaining = (batchNotes || []).filter((_, idx) => idx !== currentNoteIndex);
      if (setBatchNotes) {
        setBatchNotes(remaining);
      }

      if (remaining.length === 0) {
        toast.info('Draft discarded.');
        setInlineNoteData(null);
        setCurrentNoteIndex(0);
        setViewingNoteInline(false);
        if (onDisposition) {
          onDisposition('discard', noteData?.encounter_id, noteData?.id);
        } else if (onWriteNextNote) {
          onWriteNextNote();
        }
        return;
      }

      toast.info('Draft discarded. Reviewing next draft.');
      const newIndex = Math.min(currentNoteIndex, remaining.length - 1);
      const nextNote = remaining[newIndex];
      if (nextNote && fetchNoteData) {
        const data = await fetchNoteData(nextNote.note_id || nextNote.id);
        if (data) {
          setInlineNoteData(data);
          setCurrentNoteIndex(newIndex);
        }
      } else {
        setCurrentNoteIndex(newIndex);
      }
    } catch (e) {
      logger.error('Failed to discard draft:', e);
      toast.error('Failed to discard draft.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasBatchNotes derives from batchNotes
  }, [batchNotes, noteData, currentNoteIndex, setInlineNoteData, setCurrentNoteIndex, setViewingNoteInline, setBatchNotes, fetchNoteData, onWriteNextNote, onDisposition, confirm]);

  return (
    <SlideIn
      show={!!viewingNoteInline && !!inlineNoteData}
      direction="right"
      duration={300}
      className="fixed inset-4 z-50 flex justify-center overflow-hidden pointer-events-none sm:inset-6"
    >
    <div data-testid="note-preview" className="pointer-events-auto h-full max-h-full w-full max-w-5xl p-4 border border-blue-300 dark:border-blue-700 rounded-2xl bg-blue-50 dark:bg-blue-900 flex min-h-0 flex-col overflow-hidden shadow-xl">
      <div className="flex justify-between items-center mb-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200">
            {getNoteTypeLabel(noteData?.document_type, noteData?.session_type, noteData?.template_name)} - {(noteData?.patient_display || noteData?.client_name || 'Unknown Client')}
          </h3>
          {hasBatchNotes && (
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-300">
              <button
                onClick={() => handleNavigateNote('prev')}
                disabled={currentNoteIndex === 0}
                className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous note"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="font-medium">
                {currentNoteIndex + 1} of {(batchNotes || []).length}
              </span>
              <button
                onClick={() => handleNavigateNote('next')}
                disabled={currentNoteIndex === (batchNotes || []).length - 1}
                className="p-1 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next note"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="close-preview"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={() => setViewingNoteInline(false)}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      {editing && noteData?.id ? (
        <Suspense fallback={<div className="text-center py-8">Loading editor…</div>}>
          <NoteEditorView noteId={String(noteData.id)} onReturn={handleEditorReturn} />
        </Suspense>
      ) : (
      <>
      {/* Read-only indicator — editing happens in the canonical editor */}
      {permissions && !permissions.is_note_owner && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm text-blue-800 dark:text-blue-200">
            Reviewing a note authored by someone else. Open it in the editor to edit or sign.
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
      {isTemplateNote ? (
        isLoadingTemplate || isLoadingPermissions ? (
          <div className="text-center py-4">Loading...</div>
        ) : template ? (
          <DynamicForm
            key={noteData?.id}
            template={template}
            initialData={templateFormData}
            onChange={() => {}}
            onSubmit={() => {}}
            patientId={noteData?.encounter?.patient_id}
            practitionerId={noteData?.encounter?.practitioner_id}
            caseId={noteData?.encounter?.case_id}
            readOnly
            isNoteOwner={permissions?.is_note_owner ?? true}
            createdByName={permissions?.created_by_name ?? null}
            fieldAttribution={permissions?.field_attribution ?? null}
          />
        ) : (
          <div className="text-center py-4 text-red-600">Failed to load template</div>
        )
      ) : (
        <div className="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold mb-3 text-gray-800 dark:text-white">Generated {getNoteTypeLabel(noteData?.document_type, noteData?.session_type, noteData?.template_name)}</h2>
          <textarea
            className="w-full h-64 sm:h-80 md:h-96 p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white rounded-md text-sm leading-relaxed"
            value={noteData?.note_content || ''}
            readOnly
            aria-label={`${getNoteTypeLabel(noteData?.document_type, noteData?.session_type, noteData?.template_name)} Content`}
          />
        </div>
      )}
      </div>
      {/* Show Next Steps panel when note is in a terminal status, otherwise show disposition buttons */}
      {isTerminal ? (
        <>
        {onDisposition && (
          <div className="flex gap-2 mt-2 flex-shrink-0">
            <button
              data-testid="signed-continue"
              onClick={() => onDisposition('sign', noteData?.encounter_id, noteData?.id)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
            >
              Done — continue
            </button>
          </div>
        )}
        <NoteNextSteps
          noteId={noteData?.id}
          status={noteData?.status}
          hasBatchNotes={!!hasBatchNotes}
          currentNoteIndex={currentNoteIndex}
          batchNotesLength={(batchNotes || []).length}
          onNavigateNext={() => handleNavigateNote('next')}
          onWriteAnother={() => {
            setViewingNoteInline(false);
            if (onWriteNextNote) {
              onWriteNextNote();
            }
            window.dispatchEvent(new CustomEvent('open_encounter_selector'));
          }}
        />
        </>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2 flex-shrink-0">
          <button
            data-testid="open-in-editor"
            onClick={handleOpenInEditor}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
            title="Open this note in the canonical editor to edit and sign"
          >
            Open in Editor
          </button>
          <button
            data-testid="keep-draft-note"
            onClick={handleKeepDraft}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            title="Keep this note as a draft and continue"
          >
            Keep Draft
          </button>
          <button
            data-testid="discard-draft-note"
            onClick={handleDiscardDraft}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
          >
            Discard Draft
          </button>
          <button
            data-testid="add-photo"
            onClick={() => setShowPhotoUpload(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 00-2-2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3.86a2 2 0 00-1.664-.89l-.812 1.22A2 2 0 0016.07 4H10.07a2 2 0 00-1.664.89l-.812 1.22A2 2 0 006.93 7H6a2 2 0 00-2 2v9a2 2 0 002 2" />
            </svg>
            Add Photo
          </button>
          <button
            data-testid="export-pdf"
            onClick={() => {
              const format = "pdf";
              window.open(buildApiUrl(`/notes/${noteData?.id}/export/${format}`), '_blank');
              toast.success(`Note exported as ${format.toUpperCase()} successfully!`);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            Download as PDF
          </button>
          <div className="text-sm text-gray-600 dark:text-gray-400 ml-auto flex items-center">
            Status: <span className="ml-1 font-medium">Draft</span>
          </div>
        </div>
      )}

      {showPhotoUpload && noteData && (
        <EncounterPhotoUpload
          noteId={noteData.id}
          patientId={noteData.patient_id}
          open={showPhotoUpload}
          onClose={() => setShowPhotoUpload(false)}
        />
      )}
      </>
      )}

    </div>
    </SlideIn>
  );
}

export default NotePreview;
